import React from 'react';
import { Box, Card, CardContent, Typography, useTheme, useMediaQuery } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { IndustrySectorDistribution as IndustrySectorDistributionType } from '../../../types/utility.types';

interface IndustrySectorDistributionProps {
  data: IndustrySectorDistributionType[];
  isLoading: boolean;
}

const IndustrySectorDistribution: React.FC<IndustrySectorDistributionProps> = ({ data, isLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Industry Sector Distribution
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Filter out any items with count 0 and sort by count (descending), then alphabetically
  const chartData = data
    .filter(item => item.count > 0)
    .sort((a, b) => {
      // Primary sort: by count descending
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      // Secondary sort: alphabetically by sector name for stable ordering
      return (a.sector || 'Unknown').localeCompare(b.sector || 'Unknown');
    })
    .map(item => ({
      sector: item.sector || 'Unknown',
      count: item.count
    }))
    .slice(0, 10); // Show top 10 sectors

  // Debug logging
  console.log('IndustrySectorDistribution - raw data:', data);
  console.log('IndustrySectorDistribution - chartData:', chartData);
  console.log('IndustrySectorDistribution - chartData length:', chartData.length);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Industry Sector Distribution
        </Typography>
        {chartData.length > 0 ? (
          <Box sx={{ height: isMobile ? 350 : 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ 
                  top: 20, 
                  right: isMobile ? 10 : 30, 
                  left: isMobile ? 10 : 30, 
                  bottom: 20 
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                <XAxis 
                  type="number"
                  tick={{ fill: '#fff', fontSize: isMobile ? 10 : 12 }}
                  stroke="rgba(255, 255, 255, 0.2)"
                  domain={[0, 'auto']}
                />
                <YAxis 
                  type="category"
                  dataKey="sector"
                  tick={{ fill: '#fff', fontSize: isMobile ? 9 : 12 }}
                  stroke="rgba(255, 255, 255, 0.2)"
                  width={isMobile ? 120 : isTablet ? 160 : 210}
                  interval={0}
                />
                <Tooltip 
                  contentStyle={{
                    background: 'rgba(30, 41, 59, 0.95)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    color: 'rgba(255, 255, 255, 0.9)',
                  }}
                  formatter={(value: number) => [`${value} clients`]}
                  labelStyle={{ color: '#fff', fontWeight: 'bold' }}
                  cursor={false}
                />
                <Bar 
                  dataKey="count" 
                  fill="#10B981" 
                  radius={[0, 8, 8, 0]}
                  minPointSize={5}
                  barSize={20}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>No data available</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default IndustrySectorDistribution;
